import React, { useEffect, useRef, useState } from "react";
import { FONT_NAMES, fontStack, fontWeight } from "@hudhud/reel";
import { Icon } from "./Icon.js";
import { SAMPLE, useLoadedFonts } from "./LangFontPick.js";

/** A font drop-down: the current font on the button, every option drawn in its own face with a line of sample text. `null` = the default (`fallback`). */
export const FontPick: React.FC<{ value: string | null; onChange: (font: string | null) => void; fallback?: string; defaultLabel?: string; fonts?: readonly string[]; sample?: string; width?: number | string }> = ({ value, onChange, fallback, defaultLabel, fonts = FONT_NAMES, sample = SAMPLE.en, width = 190 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useLoadedFonts(open ? fonts : []);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", close); window.addEventListener("keydown", key);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", key); };
  }, [open]);
  const shown = value ?? fallback ?? fonts[0];
  const row = (font: string, label: string, pick: () => void, on: boolean) => (
    <button key={label} role="option" aria-selected={on} onClick={() => { pick(); setOpen(false); }} style={{ flexDirection: "column", alignItems: "stretch", gap: 2, background: on ? "var(--gold-dim)" : undefined }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: fontStack(font), fontWeight: fontWeight(font, 800), fontSize: 15 }}>{label}{on ? <Icon name="check" size={13} color="var(--gold)" /> : null}</span>
      <span className="hint" style={{ fontFamily: fontStack(font), fontWeight: fontWeight(font, 500), fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sample}</span>
    </button>
  );
  return (
    <div ref={ref} className="menuwrap" style={{ width, maxWidth: "100%" }}>
      <button className="btn sm" onClick={() => setOpen(!open)} aria-haspopup="listbox" aria-expanded={open} title="Pick a font · each one is shown as it will look" style={{ width: "100%", justifyContent: "space-between", fontFamily: fontStack(shown), fontWeight: fontWeight(shown, 700), fontSize: 13 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value == null && defaultLabel ? defaultLabel : shown}</span><Icon name="chev-down" size={13} />
      </button>
      {open ? <div className="menu" role="listbox" style={{ maxHeight: 340, overflow: "auto", minWidth: 250 }}>
        {defaultLabel && fallback ? row(fallback, defaultLabel, () => onChange(null), value == null) : null}
        {fonts.map((f) => row(f, f, () => onChange(f), value === f))}
      </div> : null}
    </div>
  );
};
